import type { User, Session } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'
import {
  getStudioSession,
  getStudioUser,
  isCogoAuthProvider,
  onStudioAuthStateChange,
  signInStudioWithGitHub,
  signInStudioWithGoogle,
  signInStudioWithPassword,
  signOutStudio,
  signUpStudio,
} from '@/services/auth-provider'
import { clearRecentProjectsOnSignOut } from './recent-projects-key'

/** Emails with admin privileges (template editing, etc.) */
const ADMIN_EMAILS: ReadonlySet<string> = new Set([
  'admin@cogo.xyz',
  'hyunsuk@cogo.xyz',
])

export const EXPLICIT_SIGN_OUT_KEY = 'cogo-studio-explicit-sign-out'

function checkIsAdmin(user: User | null): boolean {
  if (!user) return false
  // 1. Supabase app_metadata role (set via service_role key or dashboard)
  if (user.app_metadata?.role === 'admin') return true
  // 2. Email whitelist fallback
  if (user.email && ADMIN_EMAILS.has(user.email)) return true
  return false
}

/**
 * Pure derived selector — Supabase auth session -> tenant id (Phase 031 Plan 02 / D-02 / LIVEDATA-01).
 *
 * Aligns with v2.1 Phase 417 cross-user RLS backbone: the RLS subject is
 * `session.user.id`, so tenant identity for `cogo.ontology.*` 4-tool calls +
 * JsonbAboxSource reads is derived directly from the authenticated session.
 *
 * Returns `null` (NOT `""`) when no session / no user is resolvable. The empty
 * string is intentionally forbidden — a silent `""` fallback would reintroduce
 * the removed `tenantId=""` dev-guard at `ds-surface.tsx:71` (GAP-06 /
 * 030-UX-GAP-INVENTORY). Callers MUST render an explicit no-tenant UI state
 * when this returns `null` (D-02 discretion).
 *
 * Mirrors the placement + shape of `checkIsAdmin` above: pure module-level
 * helper, no zustand access, no side effects, defined above the slice factory.
 */
export function deriveTenantId(session: Session | null): string | null {
  return session?.user?.id ?? null
}

function readExplicitSignOut(): boolean {
  if (typeof window === 'undefined') return false
  return window.localStorage.getItem(EXPLICIT_SIGN_OUT_KEY) === '1'
}

function writeExplicitSignOut(value: boolean): void {
  if (typeof window === 'undefined') return
  if (value) {
    window.localStorage.setItem(EXPLICIT_SIGN_OUT_KEY, '1')
  } else {
    window.localStorage.removeItem(EXPLICIT_SIGN_OUT_KEY)
  }
}

function clearStoredSupabaseAuth(): void {
  if (typeof window === 'undefined') return
  for (const storage of [window.localStorage, window.sessionStorage]) {
    for (const key of Object.keys(storage)) {
      const isSupabaseAuthKey =
        key === 'supabase.auth.token' ||
        key.includes('supabase.auth') ||
        (key.startsWith('sb-') &&
          (key.includes('auth-token') || key.includes('code-verifier')))
      if (isSupabaseAuthKey) storage.removeItem(key)
    }
  }
}

/**
 * Sign-out storage hygiene (Studio Wave 3 Lane C, 2026-08-17) — a reviewed,
 * per-key clear-on-sign-out list.
 *
 * Source: `docs/superpowers/specs/2026-08-17-studio-slice4-logout-walk.md`'s
 * L1 census. It byte-diffed every localStorage/sessionStorage key across a
 * real sign-out and found the agentic registry blob (~3.8KB of raw chat
 * prompts + preview URLs) surviving byte-identical, plus 5 other keys
 * untouched — with the registry's own `clear()` never given a caller. This
 * function is the controller-reviewed decision the L1 report deferred: every
 * key from that census is accounted for below, cleared or not, with a
 * reason, so "silent survival" becomes a reviewed decision either way.
 * `sb-*-auth-token` / `supabase.auth` / `code-verifier` keys are already
 * handled above by `clearStoredSupabaseAuth()` and are not repeated here.
 *
 * CLEARED — user-authored content that would leak to the next signed-in
 * identity on a shared machine:
 *   - `cogo-studio:cogo-xyz-registry:v1` (the agentic registry: raw chat
 *     prompts, work items, bundles, preview URLs) via
 *     `CogoXyzRegistry.clearOnSignOut()` — that method's first caller.
 *   - `cogo-studio-recent-projects` (this user's own recently-opened
 *     project ids) via `clearRecentProjectsOnSignOut()`.
 *   - locally cached document sources (memory + IndexedDB) via
 *     `clearAllDocumentViewerSourcesForLogout()`.
 *   - locally cached OpenPencil source workspaces via
 *     `clearAllOpenPencilSourceWorkspaceCachesForLogout()`.
 *
 * NOT cleared — device/preference or non-content state, kept on purpose:
 *   - `cogo-studio-ui-v1` — panel open/closed + widths (Zustand `persist`
 *     UI whitelist, `studio-store.ts`); a device preference, not content.
 *   - `cogo-studio-theme` — light/dark/system theme choice (`use-theme.ts`);
 *     a device preference.
 *   - `studio.onboarding.v90` — onboarding completion status
 *     (`ide-v2-slice.ts`); not user content, and re-showing onboarding to a
 *     returning user is a worse experience than any leak risk here.
 *   - `cogo-studio:generation-runs:v1` — anonymous timing telemetry
 *     (durations only, `generation-run-stats.ts`); carries no prompts or
 *     project data.
 *   - `cogo-boot-trace` (sessionStorage) — boot-ladder diagnostic trace
 *     (`main.tsx`); technical, not user content.
 *   - `cogo.studio.capability.session.v1` (sessionStorage) — a session
 *     correlation id owned by `studio-capability-gateway.ts` (out of scope
 *     for this lane); holds no user content.
 *   - `cogo-studio-explicit-sign-out` (`EXPLICIT_SIGN_OUT_KEY` above) —
 *     sign-out's OWN output flag; this lane adds clearing alongside it
 *     without changing its lifecycle.
 *
 * The registry is reached through `await import(...)` rather than a static
 * import on purpose. A static `@/features/cogo-xyz/cogo-xyz-registry` edge here
 * puts that module — and, through its own re-exports, `cogo-xyz-preview-proof`
 * — inside the production dashboard dependency graph (every dashboard page
 * imports `studio-store`, which composes this slice), which the dashboard
 * authority boundary in `src/services/__tests__/dashboard-api-truthfulness.test.ts`
 * forbids. Sign-out is the only thing here that needs the registry, so it pays
 * for it at sign-out instead of at dashboard load. The `await` at the call site
 * is load-bearing: the clear must complete before the store flips to
 * signed-out.
 *
 * The `try` is load-bearing for the same reason `signOut` already tolerates a
 * failing `signOutStudio()`: a lazily-fetched chunk can 404 (a tab open across
 * a redeploy), and a rejected `import(...)` here would otherwise abort sign-out
 * AFTER the remote session and the Supabase auth keys are already gone — server
 * signed out, UI still signed in. Reaching the signed-out state wins; the
 * failure is reported, never swallowed silently.
 */
async function clearSignOutContentKeys(): Promise<void> {
  try {
    const { cogoXyzRegistry } = await import('@/features/cogo-xyz/cogo-xyz-registry')
    cogoXyzRegistry.clearOnSignOut()
  } catch (error) {
    console.error('[auth] signOut could not clear the agentic registry:', error)
  }
  try {
    const { clearAllDocumentViewerSourcesForLogout } = await import(
      '@/features/source-document/docx-source-viewer-state'
    )
    await clearAllDocumentViewerSourcesForLogout()
  } catch (error) {
    console.error('[auth] signOut could not clear local document sources:', error)
  }
  try {
    const { clearAllOpenPencilSourceWorkspaceCachesForLogout } = await import(
      '@/features/openpencil-figma/openpencil-source-workspace-storage'
    )
    clearAllOpenPencilSourceWorkspaceCachesForLogout()
  } catch (error) {
    console.error('[auth] signOut could not clear local OpenPencil sources:', error)
  }
  clearRecentProjectsOnSignOut()
}

export interface Subscription {
  plan_type: string
  billing_cycle: string
  status: string
}

export interface AuthSlice {
  user: User | null
  session: Session | null
  isAuthLoading: boolean
  isAdmin: boolean
  explicitSignOut: boolean
  forceMockChat: boolean
  subscription: Subscription | null

  initAuth: () => Promise<void>
  signIn: (email: string, password: string) => Promise<void>
  signInWithGoogle: () => Promise<void>
  signInWithGitHub: () => Promise<void>
  signUp: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
  toggleForceMock: () => void
  loadSubscription: () => Promise<void>
}

type Set = (fn: (state: AuthSlice) => void) => void

export function createAuthSlice(
  set: Set,
  _get: unknown,
  _api: unknown,
): AuthSlice {
  return {
    user: null,
    session: null,
    isAuthLoading: true,
    isAdmin: false,
    explicitSignOut: readExplicitSignOut(),
    forceMockChat: false,
    subscription: null,

    initAuth: async () => {
      const { data: { session } } = await getStudioSession()
      const explicitSignOut = readExplicitSignOut()
      const effectiveSession = explicitSignOut ? null : session
      set((s) => {
        s.user = effectiveSession?.user ?? null
        s.session = effectiveSession
        s.isAdmin = checkIsAdmin(effectiveSession?.user ?? null)
        s.explicitSignOut = explicitSignOut
        s.isAuthLoading = false
      })

      // Load active subscription
      if (!isCogoAuthProvider && effectiveSession?.user) {
        supabase
          .from('subscriptions')
          .select('plan_type, billing_cycle, status')
          .eq('user_id', effectiveSession.user.id)
          .eq('status', 'active')
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle()
          .then(({ data }) => {
            set((s) => { s.subscription = data })
          })
      }

      onStudioAuthStateChange((_event, session) => {
        const explicitSignOut = readExplicitSignOut()
        const effectiveSession = explicitSignOut ? null : session
        set((s) => {
          s.user = effectiveSession?.user ?? null
          s.session = effectiveSession
          s.isAdmin = checkIsAdmin(effectiveSession?.user ?? null)
          s.explicitSignOut = explicitSignOut
        })
      })
    },

    signIn: async (email, password) => {
      const { data, error } = await signInStudioWithPassword({ email, password })
      if (error) throw error
      writeExplicitSignOut(false)
      const nextSession = data?.session ?? null
      const nextUser = nextSession?.user ?? data?.user ?? null
      set((s) => {
        s.user = nextUser
        s.session = nextSession
        s.isAdmin = checkIsAdmin(nextUser)
        s.explicitSignOut = false
      })
    },

    signInWithGoogle: async () => {
      const { error } = await signInStudioWithGoogle()
      if (error) throw error
      writeExplicitSignOut(false)
      set((s) => { s.explicitSignOut = false })
    },

    signInWithGitHub: async () => {
      const { error } = await signInStudioWithGitHub()
      if (error) throw error
      writeExplicitSignOut(false)
      set((s) => { s.explicitSignOut = false })
    },

    signUp: async (email, password) => {
      const { data, error } = await signUpStudio({ email, password })
      if (error) throw error
      writeExplicitSignOut(false)
      const nextSession = data?.session ?? null
      const nextUser = nextSession?.user ?? data?.user ?? null
      set((s) => {
        s.user = nextUser
        s.session = nextSession
        s.isAdmin = checkIsAdmin(nextUser)
        s.explicitSignOut = false
      })
    },

    signOut: async () => {
      writeExplicitSignOut(true)
      let signOutError: unknown
      try {
        const { error } = await signOutStudio()
        signOutError = error
      } catch (error) {
        signOutError = error
      }
      if (signOutError) {
        console.warn('[auth] signOut failed; clearing local session anyway:', signOutError)
      }
      clearStoredSupabaseAuth()
      await clearSignOutContentKeys()
      set((s) => {
        s.user = null
        s.session = null
        s.isAdmin = false
        s.explicitSignOut = true
        s.subscription = null
      })
    },

    toggleForceMock: () => set((s) => { s.forceMockChat = !s.forceMockChat }),

    loadSubscription: async () => {
      if (isCogoAuthProvider) {
        set((s) => { s.subscription = null })
        return
      }
      const { data: { user } } = await getStudioUser()
      if (!user) return
      const { data } = await supabase
        .from('subscriptions')
        .select('plan_type, billing_cycle, status')
        .eq('user_id', user.id)
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle()
      set((s) => { s.subscription = data })
    },
  }
}
